import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bot, X, Sparkles, ExternalLink, Mic, Minus } from 'lucide-react';
import AgentEmbedPage from './AgentEmbedPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── sectionContext → agent category in DB ─────────────────────────────────────
const SECTION_CATEGORY_MAP = {
  marketplace: 'marketplace',
  health: 'salud', salud: 'salud',
  travel: 'viajes', viajes: 'viajes',
  finance: 'finanzas', finanzas: 'finanzas',
  music: 'musica', musica: 'musica',
  notes: 'notas', notas: 'notas',
  calendar: 'calendario', calendario: 'calendario',
  documents: 'documentos', documentos: 'documentos',
  lists: 'listas', listas: 'listas',
  reminders: 'recordatorios', recordatorios: 'recordatorios',
  memes: 'entretenimiento', movies: 'entretenimiento', cine: 'entretenimiento',
  wallet: 'finanzas',
  support: 'soporte', soporte: 'soporte',
};

// ─── Section → branded display (overrides agent.name in banner only) ──────────
const SECTION_BRAND = {
  marketplace:    { name: 'OkBot Mercader',   emoji: '🛒', color: '#f59e0b' },
  health:         { name: 'OkBot Doctor',     emoji: '🩺', color: '#06b6d4' },
  salud:          { name: 'OkBot Doctor',     emoji: '🩺', color: '#06b6d4' },
  travel:         { name: 'OkBot Agente',     emoji: '✈️', color: '#a855f7' },
  viajes:         { name: 'OkBot Agente',     emoji: '✈️', color: '#a855f7' },
  finance:        { name: 'OkBot Financiero', emoji: '💰', color: '#10b981' },
  finanzas:       { name: 'OkBot Financiero', emoji: '💰', color: '#10b981' },
  music:          { name: 'OkBot Melómano',   emoji: '🎵', color: '#ec4899' },
  musica:         { name: 'OkBot Melómano',   emoji: '🎵', color: '#ec4899' },
  notes:          { name: 'OkBot Secretario', emoji: '📋', color: '#3b82f6' },
  notas:          { name: 'OkBot Secretario', emoji: '📋', color: '#3b82f6' },
  calendar:       { name: 'OkBot Secretario', emoji: '📅', color: '#3b82f6' },
  calendario:     { name: 'OkBot Secretario', emoji: '📅', color: '#3b82f6' },
  reminders:      { name: 'OkBot Secretario', emoji: '⏰', color: '#3b82f6' },
  recordatorios:  { name: 'OkBot Secretario', emoji: '⏰', color: '#3b82f6' },
  lists:          { name: 'OkBot Secretario', emoji: '✅', color: '#3b82f6' },
  listas:         { name: 'OkBot Secretario', emoji: '✅', color: '#3b82f6' },
  documents:      { name: 'OkBot Profesor',   emoji: '📚', color: '#6366f1' },
  documentos:     { name: 'OkBot Profesor',   emoji: '📚', color: '#6366f1' },
  cine:           { name: 'OkBot Cinéfilo',   emoji: '🎬', color: '#ef4444' },
  movies:         { name: 'OkBot Cinéfilo',   emoji: '🎬', color: '#ef4444' },
  memes:          { name: 'OkBot Cinéfilo',   emoji: '😂', color: '#ef4444' },
  support:        { name: 'OkBot Soporte',    emoji: '🛟', color: '#0ea5e9' },
  soporte:        { name: 'OkBot Soporte',    emoji: '🛟', color: '#0ea5e9' },
};

const matchBrand = (sectionContext = '') => {
  const lc = sectionContext.toLowerCase();
  for (const key of Object.keys(SECTION_BRAND)) {
    if (lc.includes(key)) return { key, ...SECTION_BRAND[key] };
  }
  return null;
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AIAgentModule = ({
  sectionContext = '',
  placeholder = '¿En qué puedo ayudarte?',  // eslint-disable-line no-unused-vars
  className = '',
  compact = false,
}) => {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);   // compact mode: chat panel open?
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => { findAgent(); }, [sectionContext]); // eslint-disable-line react-hooks/exhaustive-deps

  const findAgent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-agents/`);
      const allAgents = (res.data || []).filter(a => a.is_active !== false);
      const lc = sectionContext?.toLowerCase() || '';
      let expectedCategory = SECTION_CATEGORY_MAP[lc];
      if (!expectedCategory) {
        for (const k of Object.keys(SECTION_CATEGORY_MAP)) {
          if (lc.includes(k)) { expectedCategory = SECTION_CATEGORY_MAP[k]; break; }
        }
      }
      let found = null;
      if (expectedCategory) found = allAgents.find(a => a.category === expectedCategory);
      if (!found && allAgents.length > 0) found = allAgents.find(a => a.category === 'usuario') || allAgents[0];
      setAgent(found || null);
    } catch (e) {
      console.error('Error loading agents:', e);
    } finally {
      setLoading(false);
    }
  };

  const brand = matchBrand(sectionContext);
  const displayName = brand?.name || agent?.name || 'OkBot IA';
  const displayColor = brand?.color || agent?.color || '#ef4444';
  const displayEmoji = brand?.emoji || '🤖';

  const triggerVoice = () => {
    // Always set pending intent so the embed picks it up on mount/ready
    try { window.__okbotPendingVoice = { agentId: agent?.id, ts: Date.now() }; } catch (_) {}
    if (!expanded) setExpanded(true);
    // Fire event for already-mounted embeds
    window.dispatchEvent(new CustomEvent('okbot:start-voice', { detail: { agentId: agent?.id } }));
  };

  // ── INLINE MODE (non-compact) — kept for back-compat (admin embeds) ─────────
  if (!compact) {
    if (loading) return null;
    if (!agent) {
      return (
        <div className={`glass-panel rounded-2xl p-8 flex flex-col items-center justify-center gap-3 ${className}`} style={{ minHeight: 200 }}>
          <Bot size={28} className="text-gray-500" />
          <p className="text-gray-400 font-semibold text-sm">Sin agente configurado</p>
          <p className="text-gray-600 text-xs text-center max-w-xs">
            Crea un agente IA desde el panel de Super Administrador
          </p>
        </div>
      );
    }
    return (
      <div className={`glass-panel rounded-2xl overflow-hidden flex flex-col ${className}`} style={{ minHeight: 460 }}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between" style={{ background: `${displayColor}10` }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg" style={{ background: `${displayColor}25` }}>
              <span>{displayEmoji}</span>
            </div>
            <div>
              <p className="text-white font-medium text-sm">{displayName}</p>
              <p className="text-xs flex items-center gap-1" style={{ color: '#4ade80' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                Agente activo
              </p>
            </div>
          </div>
          <button onClick={() => setFullscreen(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white hover:bg-white/10">
            <ExternalLink size={12} />Expandir
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 380 }}>
          <AgentEmbedPage agentId={agent.id} />
        </div>
        {fullscreen && (
          <FullscreenModal agent={agent} color={displayColor} onClose={() => setFullscreen(false)} />
        )}
      </div>
    );
  }

  // ── COMPACT MODE — minimizable embedded chat + mic FAB + banner ─────────────
  if (loading) {
    return (
      <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3">
        <div className="w-14 h-14 rounded-full bg-gray-800 border border-white/10 flex items-center justify-center">
          <Bot size={20} className="text-gray-600 animate-pulse" />
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
        <div className="bg-black/70 backdrop-blur-sm border border-white/15 rounded-2xl px-3 py-2 text-xs text-gray-400 whitespace-nowrap shadow-xl flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse inline-block" />
          Sin agente configurado
        </div>
        <div className="w-14 h-14 rounded-full bg-gray-800/80 border border-white/10 flex items-center justify-center opacity-50">
          <Bot size={22} className="text-gray-500" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Minimizable embedded chat panel — fixed bottom-right */}
      {expanded && (
        <div
          className="fixed z-40 flex flex-col rounded-2xl overflow-hidden shadow-2xl animate-scale-in"
          style={{
            right: 24,
            bottom: 96,
            width: 'min(380px, calc(100vw - 32px))',
            height: 'min(560px, calc(100vh - 140px))',
            background: '#0d0d0f',
            border: `1px solid ${displayColor}40`,
          }}
        >
          {/* Banner header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${displayColor}30, ${displayColor}10)`, borderBottom: `1px solid ${displayColor}30` }}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 relative overflow-hidden" style={{ background: displayColor }}>
                <img src="/okbot_dark_theme.png" alt="OkBot" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                <span style={{ position: 'absolute', bottom: -2, right: -2, fontSize: 14, lineHeight: 1, background: '#0d0d0f', borderRadius: 999, padding: 1 }}>{displayEmoji}</span>
              </div>
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm truncate">{displayName}</p>
                <p className="text-xs flex items-center gap-1.5 text-green-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" />
                  En línea
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setFullscreen(true)} title="Pantalla completa"
                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
                <ExternalLink size={14} />
              </button>
              <button onClick={() => setExpanded(false)} title="Minimizar"
                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10">
                <Minus size={16} />
              </button>
            </div>
          </div>
          {/* Chat body */}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AgentEmbedPage agentId={agent.id} />
          </div>
        </div>
      )}

      {/* Floating action stack — bottom-right */}
      <div className={`fixed bottom-6 right-6 z-40 flex items-end gap-3 ${className}`}>
        {/* Red mic FAB */}
        <button
          onClick={triggerVoice}
          title="Hablar con el agente"
          className="w-12 h-12 rounded-full text-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{ background: '#dc2626', border: '2px solid #b91c1c' }}
        >
          <Mic size={18} />
        </button>

        {/* Agent FAB */}
        <div className="flex flex-col items-end gap-2">
          {!expanded && (
            <div className="bg-black/80 backdrop-blur-sm border border-white/15 rounded-xl px-3 py-1.5 text-xs text-white whitespace-nowrap shadow-xl flex items-center gap-1.5">
              <span>{displayEmoji}</span>
              <span>{displayName}</span>
            </div>
          )}
          <button
            onClick={() => setExpanded(p => !p)}
            className="w-14 h-14 rounded-full text-white shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95 overflow-hidden"
            style={{ background: expanded ? '#374151' : displayColor, border: `2px solid ${expanded ? '#6b7280' : displayColor}` }}
            title={displayName}
          >
            {expanded
              ? <X size={20} />
              : <img src="/okbot_dark_theme.png" alt="OkBot" style={{ width: 32, height: 32, objectFit: 'contain' }} />}
          </button>
        </div>
      </div>

      {fullscreen && (
        <FullscreenModal agent={agent} color={displayColor} onClose={() => setFullscreen(false)} />
      )}
    </>
  );
};

// ─── Full-screen Agent Modal ─────────────────────────────────────────────────
const FullscreenModal = ({ agent, color, onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
    style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
    onClick={onClose}
  >
    <div
      className="w-full max-w-md flex flex-col rounded-2xl overflow-hidden shadow-2xl animate-scale-in"
      style={{ height: 'min(680px, 90vh)', border: `1px solid ${color}30`, background: '#0d0d0f' }}
      onClick={e => e.stopPropagation()}
    >
      <AgentEmbedPage agentId={agent.id} onClose={onClose} />
    </div>
  </div>
);

export default AIAgentModule;
